import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');
  const resetToken = token || tokenFromQuery;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [resetToken]);

  const validateToken = async () => {
    if (!resetToken) {
      setTokenValid(false);
      setValidating(false);
      return;
    }

    try {
      await axios.get(`${API}/api/auth/validate-reset-token/${resetToken}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: resetToken,
        new_password: password
      });
      setSuccess(true);
      toast.success('Senha alterada com sucesso!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao redefinir senha';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-8 h-8 text-vocalira animate-spin mx-auto mb-4" />
          <p className="text-dark-muted">Validando link...</p>
        </motion.div>
      </div>
    );
  }

  // Invalid/Expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-dark-card rounded-3xl p-8 border border-dark-border shadow-dark-medium text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text mb-3">
              Link inválido ou expirado
            </h1>
            
            <p className="text-dark-muted mb-6">
              Este link de recuperação não é mais válido. Links expiram após 1 hora por segurança.
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full py-6"
              >
                Solicitar novo link
              </Button>
              
              <button
                onClick={() => navigate('/login')}
                className="text-dark-muted hover:text-vocalira text-sm transition-colors"
              >
                Voltar ao login
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-dark-card rounded-3xl p-8 border border-dark-border shadow-dark-medium text-center">
            <div className="w-16 h-16 bg-cyan/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-cyan" />
            </div>
            
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text mb-3">
              Senha alterada!
            </h1>
            
            <p className="text-dark-muted mb-6">
              Sua nova senha foi definida com sucesso. Agora você pode fazer login.
            </p>

            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full py-6"
            >
              Fazer login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-dark-card rounded-3xl p-8 border border-dark-border shadow-dark-medium">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-vocalira/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-vocalira" />
            </div>
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text mb-2">
              Criar nova senha
            </h1>
            <p className="text-dark-muted">
              Digite sua nova senha abaixo
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-dark-text font-medium">
                Nova senha
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-dark-elevated border-dark-border text-dark-text rounded-xl px-4 py-3 h-12 pr-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-dark-text font-medium">
                Confirmar senha
              </Label>
              <div className="relative mt-2">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  data-testid="reset-password-confirm-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-dark-elevated border-dark-border text-dark-text rounded-xl px-4 py-3 h-12 pr-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira"
                  placeholder="Digite novamente"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-400 text-sm mt-2">As senhas não coincidem</p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="reset-password-submit"
              disabled={loading || password !== confirmPassword}
              className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full py-6 font-medium shadow-glow disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar nova senha'
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
